import React from 'react';
import useSourcesUploadQueueStore, {getQueuedFile} from './useSourcesUploadQueueStore';
import {
    createSourceFromContent,
    createSourceFromUrl,
    createSourcesFolderInParent,
    fetchSourcesFoldersByParent,
    fetchUploadQueue,
    uploadSourceFile,
    SourceUploadDuplicateError,
} from './sourcesApi';

function isAbortError(error) {
    return error?.name === 'AbortError' || error?.name === 'CanceledError';
}

export default function useSourcesUploadQueue({userId, onUploaded, onDuplicate, enabled = true}) {
    const items = useSourcesUploadQueueStore((state) => state.items);
    const paused = useSourcesUploadQueueStore((state) => state.paused);
    const concurrency = useSourcesUploadQueueStore((state) => state.concurrency);
    const hydrateFromServer = useSourcesUploadQueueStore((state) => state.hydrateFromServer);
    const markUploading = useSourcesUploadQueueStore((state) => state.markUploading);
    const markProgress = useSourcesUploadQueueStore((state) => state.markProgress);
    const markSuccess = useSourcesUploadQueueStore((state) => state.markSuccess);
    const markFailed = useSourcesUploadQueueStore((state) => state.markFailed);
    const markDuplicate = useSourcesUploadQueueStore((state) => state.markDuplicate);
    const markCancelled = useSourcesUploadQueueStore((state) => state.markCancelled);
    const markPaused = useSourcesUploadQueueStore((state) => state.markPaused);
    const clearAll = useSourcesUploadQueueStore((state) => state.clearAll);

    const inFlightControllersRef = React.useRef(new Map());
    const folderCacheRef = React.useRef(new Map());
    const parentFoldersCacheRef = React.useRef(new Map());
    const folderResolveInFlightRef = React.useRef(new Map());

    // On mount, fetch the server's in-flight pipeline items and merge them into the queue.
    // Runs once per mount (not a polling loop). Non-fatal if the fetch fails.
    React.useEffect(() => {
        if (!enabled || !userId) {
            return;
        }

        let cancelled = false;
        const controller = new AbortController();

        fetchUploadQueue({userId, signal: controller.signal})
            .then((serverItems) => {
                if (!cancelled) {
                    hydrateFromServer(serverItems);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
            controller.abort();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, userId]); // intentionally omit hydrateFromServer (stable store action)

    // Poll every 5 s while any items are in 'processing' state (pipeline running after XHR upload).
    // When the server reports 'processed', hydrateFromServer promotes them to 'success'.
    // The interval stops automatically when there are no more processing items.
    React.useEffect(() => {
        if (!enabled || !userId) {
            return;
        }

        const hasProcessing = items.some(
            (item) => item.status === 'processing' && item.canonicalItemId,
        );

        if (!hasProcessing) {
            return;
        }

        let cancelled = false;
        const tid = setTimeout(async () => {
            if (cancelled) return;
            try {
                const serverItems = await fetchUploadQueue({userId});
                if (!cancelled) {
                    hydrateFromServer(serverItems);
                }
            } catch (_) {
                // Non-fatal — next tick will retry
            }
        }, 5000);

        return () => {
            cancelled = true;
            clearTimeout(tid);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, userId, items]); // items dep re-arms the timer after each update

    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        if (!paused) {
            return;
        }

        const inFlight = Array.from(inFlightControllersRef.current.entries());
        inFlight.forEach(([itemId, controller]) => {
            controller.abort();
            markPaused(itemId);
        });
    }, [enabled, markPaused, paused]);

    const ensureParentFolderCache = React.useCallback(async (parentFolderId, signal) => {
        const parentKey = parentFolderId || 'root';
        if (parentFoldersCacheRef.current.has(parentKey)) {
            return parentFoldersCacheRef.current.get(parentKey);
        }

        const folders = await fetchSourcesFoldersByParent({
            userId,
            parentFolderId,
            signal,
        });

        const folderNameMap = new Map();
        folders.forEach((folder) => {
            const key = String(folder.name || '').trim().toLowerCase();
            if (!key || folderNameMap.has(key)) {
                return;
            }
            folderNameMap.set(key, folder.id);
            folderCacheRef.current.set(`${parentKey}::${String(folder.name || '').trim()}`, folder.id);
        });
        parentFoldersCacheRef.current.set(parentKey, folderNameMap);
        return folderNameMap;
    }, [userId]);

    const ensureFolderExists = React.useCallback(async ({parentFolderId, folderName, signal}) => {
        const parentKey = parentFolderId || 'root';
        const normalizedName = String(folderName || '').trim();
        const folderKey = `${parentKey}::${normalizedName}`;
        if (folderCacheRef.current.has(folderKey)) {
            return folderCacheRef.current.get(folderKey);
        }

        const existingInFlight = folderResolveInFlightRef.current.get(folderKey);
        if (existingInFlight) {
            return existingInFlight;
        }

        const resolverPromise = (async () => {
            const parentMap = await ensureParentFolderCache(parentFolderId, signal);
            const normalizedLookupName = normalizedName.toLowerCase();
            const existingFolderId = parentMap.get(normalizedLookupName);
            if (existingFolderId) {
                folderCacheRef.current.set(folderKey, existingFolderId);
                return existingFolderId;
            }

            const createdFolder = await createSourcesFolderInParent({
                userId,
                name: normalizedName,
                parentFolderId,
                signal,
            });

            parentMap.set(normalizedLookupName, createdFolder.id);
            folderCacheRef.current.set(folderKey, createdFolder.id);
            return createdFolder.id;
        })();

        folderResolveInFlightRef.current.set(folderKey, resolverPromise);

        try {
            return await resolverPromise;
        } finally {
            folderResolveInFlightRef.current.delete(folderKey);
        }
    }, [ensureParentFolderCache, userId]);

    const resolveFolderIdForItem = React.useCallback(async (item, signal) => {
        let currentParentId = item.targetFolderId || null;
        const folderPath = Array.isArray(item.folderPath) ? item.folderPath : [];

        for (const folderNameRaw of folderPath) {
            const folderName = String(folderNameRaw || '').trim();
            if (!folderName) {
                continue;
            }
            currentParentId = await ensureFolderExists({
                parentFolderId: currentParentId,
                folderName,
                signal,
            });
        }

        return currentParentId;
    }, [ensureFolderExists]);

    const startUpload = React.useCallback(async (item) => {
        if (!item?.id) {
            return;
        }

        if (inFlightControllersRef.current.has(item.id)) {
            return;
        }

        if (!userId) {
            markFailed(item.id, 'User ID is required to upload files.');
            return;
        }

        const isWebImport = item.sourceKind === 'url' || item.sourceKind === 'paste';

        const file = isWebImport ? null : getQueuedFile(item.fileKey);
        if (!isWebImport && !file) {
            markFailed(item.id, 'File data is unavailable. Add the file again and retry.');
            return;
        }

        const controller = new AbortController();
        inFlightControllersRef.current.set(item.id, controller);
        markUploading(item.id);

        try {
            const parentFolderId = await resolveFolderIdForItem(item, controller.signal);

            if (isWebImport) {
                markProgress(item.id, 50);
                const importedItem = item.sourceKind === 'url'
                    ? await createSourceFromUrl({
                        url: item.url,
                        userId,
                        parentFolderId,
                        // Do not send deriveWebImportName (hostname). The API extracts
                        // <title> / og:title / twitter:title (MINSKY-17394).
                        name: null,
                        classificationLevel: item.classificationLevel || null,
                        distribution: item.distribution,
                        compartment: item.compartment,
                        signal: controller.signal,
                    })
                    : await createSourceFromContent({
                        htmlContent: item.htmlContent,
                        plainText: item.plainText,
                        name: item.name,
                        userId,
                        parentFolderId,
                        classificationLevel: item.classificationLevel || null,
                        distribution: item.distribution,
                        compartment: item.compartment,
                        signal: controller.signal,
                    });
                markSuccess(item.id, {
                    uploadedFileId: importedItem.id,
                    serverStatus: importedItem.status || null,
                });
                onUploaded?.(importedItem);
                return;
            }

            const uploadedFile = await uploadSourceFile({
                file,
                userId,
                parentFolderId,
                classificationLevel: item.classificationLevel || null,
                distribution: item.distribution,
                compartment: item.compartment,
                signal: controller.signal,
                onProgress: ({progress}) => {
                    markProgress(item.id, progress);
                },
            });

            markSuccess(item.id, {
                uploadedFileId: uploadedFile.id,
                canonicalItemId: uploadedFile.canonicalItemId || null,
                serverStatus: uploadedFile.status || null,
                stage: uploadedFile.stage || null,
                progressPercent: uploadedFile.progressPercent ?? null,
            });
            onUploaded?.(uploadedFile);
        } catch (error) {
            if (isAbortError(error)) {
                const latestState = useSourcesUploadQueueStore.getState();
                const latestItem = latestState.items.find((candidate) => candidate.id === item.id);
                if (!latestItem || latestItem.status === 'paused' || latestItem.status === 'cancelled') {
                    return;
                }

                if (latestState.paused) {
                    markPaused(item.id);
                    return;
                }

                markCancelled(item.id, 'Canceled');
                return;
            }

            if (error instanceof SourceUploadDuplicateError) {
                markDuplicate(item.id, error.message, error.referenceId);
                onDuplicate?.({
                    fileName: file?.name || item.name,
                    referenceId: error.referenceId,
                    message: error.message,
                });
                return;
            }

            markFailed(item.id, error?.message || 'Upload failed');
        } finally {
            inFlightControllersRef.current.delete(item.id);
        }
    }, [markCancelled, markDuplicate, markFailed, markPaused, markProgress, markSuccess, markUploading, onDuplicate, onUploaded, resolveFolderIdForItem, userId]);

    React.useEffect(() => {
        if (!enabled || paused || !userId) {
            return;
        }

        const uploadingCount = items.filter((item) => item.status === 'uploading').length;
        const slots = Math.max(0, concurrency - uploadingCount);

        if (!slots) {
            return;
        }

        const queued = items
            .filter((item) => item.status === 'queued' && !inFlightControllersRef.current.has(item.id))
            .slice(0, slots);

        queued.forEach((item) => {
            startUpload(item).catch(() => {});
        });
    }, [concurrency, enabled, items, paused, startUpload, userId]);

    const cancelItem = React.useCallback((itemId) => {
        const controller = inFlightControllersRef.current.get(itemId);
        if (controller) {
            controller.abort();
        } else {
            markCancelled(itemId, 'Canceled');
        }
    }, [markCancelled]);

    const clearQueue = React.useCallback(() => {
        const inFlight = Array.from(inFlightControllersRef.current.values());
        inFlight.forEach((controller) => controller.abort());
        inFlightControllersRef.current.clear();
        clearAll();
    }, [clearAll]);

    return {
        cancelItem,
        clearQueue,
    };
}
